import { Box, Grid, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { routes } from "../../constants/routes";
import { ContentfulContext } from "../../context/ContentfulContext";
import { Asset, Footer as FooterModel } from "../../models/schema";
import { colors } from "../../theme/colors";
import { replaceParam, smoothScroll } from "../../utils/utilities";
import { ExternalLink } from "../utils/ExternalLink";
import { InternalLink } from "../utils/InternalLink";
import NavigationPanel from "../utils/NavigationPanel";
import SocialButton from "../utils/SocialButton";
import CookieConsent from "../view/CookieConsent";
import Logo from "./Logo";

export const FOOTER_ID = "footer";

const Footer = () => {
  const { content } = useContext(ContentfulContext);
  const footer = content?.footer as FooterModel;

  const [anchorToNavigate, setAnchorToNavigate] = useState<string | null>(null);
  const [showTopCurve, setShowTopCurve] = useState(true);

  const location = useLocation();

  useEffect(() => {
    if (anchorToNavigate) {
      smoothScroll(anchorToNavigate);
      setAnchorToNavigate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    const enabledPages = [routes.legal, routes.termsOfUse];
    const showTopCurve = enabledPages.includes(location.pathname);

    setShowTopCurve(showTopCurve);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <footer style={{ flexShrink: 0, width: "100%", position: "relative" }} id={FOOTER_ID}>
      {showTopCurve && (
        <Box
          sx={{
            width: "100%",
            height: "5rem",
            borderRadius: "100% 0% 100% 0%/0% 80% 30% 100%",
            zIndex: 200,
            position: "absolute",
            top: 0,
            left: 0,
            background: colors.white
          }}
        />
      )}
      <Box
        sx={{
          width: "100%",
          px: { xs: 4, md: 6 },
          pb: { xs: 4, md: 6 },
          pt: 20,
          color: "white",
          backgroundColor: colors.grey4
        }}>
        <Grid container spacing={6}>
          <Grid
            item
            xs={12}
            md={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            }}>
            <Link
              to={routes.home}
              style={{
                textDecoration: "none",
                color: "inherit",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                marginTop: "0.8rem"
              }}>
              <Logo size="large" logo={footer.logo} />
            </Link>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}
            mt={1}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            }}>
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                mt: 2.5,
                width: "100%",
                whiteSpace: "nowrap"
              }}>
              <NavigationPanel variant="footer" />
            </Box>
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              <NavigationPanel variant="footer" mobile />
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={3}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            }}>
            <Typography mb={5}>{footer.followUs}</Typography>
            <Box
              sx={{
                display: "flex",
                "& > :not(template) ~ :not(template)": {
                  ml: 2
                }
              }}>
              {footer.socialConnectionsCollection?.items.map((socialConnection) => (
                <SocialButton
                  grayScale
                  key={socialConnection?.title}
                  link={socialConnection?.url as string}
                  logo={socialConnection?.logo as Asset}
                />
              ))}
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={3}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center"
            }}>
            <Typography>{footer.contact}</Typography>
            <Typography sx={{ mt: 3 }}>
              <ExternalLink sx={{ color: colors.yellow }} href={`tel:${footer.phoneNumber}`}>
                {footer.phoneNumber}
              </ExternalLink>
            </Typography>
            <Typography sx={{ mt: 3 }}>
              <ExternalLink href={`mailto:${footer.email}`}>{footer.email}</ExternalLink>
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexDirection: { xs: "column", md: "row" },
          p: 4,
          backgroundColor: colors.black,
          color: colors.white
        }}>
        <Box sx={{ mr: { xs: 0, md: 5 } }}>
          <CookieConsent />
        </Box>
        <InternalLink
          to={routes.legal}
          sx={{
            mt: { xs: 5, md: 0 },
            mr: { xs: 0, md: 5 },
            fontSize: { xs: "0.69rem", md: "0.83rem" }
          }}>
          {footer.legalInfo}
        </InternalLink>
        <InternalLink
          to={routes.termsOfUse}
          sx={{
            mt: { xs: 5, md: 0 },
            mr: { xs: 0, md: 5 },
            fontSize: { xs: "0.69rem", md: "0.83rem" }
          }}>
          {footer.termsOfUse}
        </InternalLink>
        <Typography
          sx={{
            mt: { xs: 5, md: 0 },
            ml: { xs: "none", md: "auto" },
            fontSize: { xs: "0.69rem", md: "0.83rem" }
          }}>
          {replaceParam(
            footer.copyrightInfo as string,
            "currentYear",
            new Date().getFullYear().toString()
          )}
        </Typography>
      </Box>
    </footer>
  );
};

export default Footer;
